/**
 * help.ts — the (?) help modal for the Kitchen demo.
 *
 * Explains the REGIME (what's being learned), the ENVIRONMENT (the world
 * the agent acts in), and how the AGENT OPERATES (routing + training +
 * the four arms). No reframe narrative — this is a user manual, not a
 * changelog.
 *
 * Singleton: open is idempotent; toggle() opens/closes. Click outside,
 * click ×, or press Esc to close.
 */

/** The blog post that accompanies this demo. */
export const ESSAY_URL = 'https://blog.vski.ai/posts/kitchen-nonlinear/';

export class HelpModal {
  readonly el: HTMLElement;
  private mounted = false;
  private onKey: (e: KeyboardEvent) => void;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'overlay help-overlay';
    this.el.innerHTML = `
      <div class="overlay-card help-card">
        <button class="help-close" aria-label="Close">×</button>
        <h2>Kitchen</h2>
        <p class="help-lead">An agent learns which items to pick up and which to skip — when an item's value depends on the <em>time of day</em>. "Good" is an interaction term (<code>item × context</code>) that a linear model provably cannot represent but a tree can.</p>

        <h3>Regime</h3>
        <p>There are 8 item types scattered on a 15×15 grid. Each is worth a value that <strong>depends on the time of day</strong>:</p>
        <ul class="help-list">
          <li>☕ coffee, 🍺 beer, 🥚 eggs, 🥛 milk — <strong>flip items</strong>: +3 in one context, −3 in the other.</li>
          <li>🌾 flour, 🍯 sugar, 🍞 toast, 🧃 juice — <strong>stable items</strong>: +1 in both contexts.</li>
        </ul>
        <p>The basket holds at most 4 item types (a set — duplicates are impossible). To collect the best basket the agent must <strong>select</strong>: it cannot just pick up everything, and on the 4 flip items it must pick up the ones the current context rewards and skip the ones it punishes. The optimum is 2 correct flips + 2 stables = +8.</p>

        <h3>Environment</h3>
        <p>Each step the environment presents the <strong>next undecided item</strong> — the nearest uncollected item by BFS from the agent's current cell. The agent then makes one binary decision: <strong>pickup</strong> or <strong>skip</strong>. The item is marked decided either way (no second chance). The episode ends when the basket is full, the step cap is reached, or no undecided items remain.</p>
        <p>Movement is <strong>not learned</strong> — the BFS walk to the next item is a fixed policy owned by the environment. The only learned decision is pickup vs skip. Picking up an item whose type is already in the basket is structurally forbidden (PICKUP is masked illegal), so the agent never wastes a slot on a duplicate.</p>

        <h3>How the agent operates</h3>
        <p>Each decision is scored over a 20-dim feature vector: the item one-hot, the current basket one-hot, the context one-hot, and the action one-hot. The head predicts the reward of <em>both</em> actions; PICKUP is chosen iff its predicted reward beats SKIP.</p>
        <p><strong>Counterfactual training:</strong> at every decision the harness knows what both arms would have yielded (skip → 0; pickup → the basket-score delta). It trains the head on both rows, so the tree sees balanced 2-arm data and can contrast pickup vs skip directly. This is the single biggest reason convergence is fast.</p>
        <p><strong>Adaptation:</strong> batch-refit every 8 decisions + an EWMA drift detector on the prediction-error stream. When the context flips mid-episode the model starts mispredicting, the detector fires, and the agent re-explores + refits.</p>

        <h3>The four arms</h3>
        <ul class="help-list">
          <li><span style="color:var(--accent)"><strong>ts</strong></span> — tree ensemble, Thompson-samples one tree per action. The headline arm: explores uncertain items, exploits confident ones.</li>
          <li><span style="color:var(--warn)"><strong>greedy</strong></span> — same tree ensemble, argmax-mean. No exploration; a sanity check on the trees.</li>
          <li><span style="color:#c44e52"><strong>linear</strong></span> — ridge closed-form Q over the <em>same</em> 20 features. <strong>The foil.</strong> Its dot product has no item×context cross term, so it averages each flip item's value across contexts to ≈ 0 and cannot flip its decision with the context. It aces the 4 stables and collapses on the 4 flips. This is the load-bearing negative result.</li>
          <li><span style="color:var(--text-dim)"><strong>random</strong></span> — coin-flip pickup/skip over legal actions. The baseline floor.</li>
        </ul>

        <h3>How to read the canvas</h3>
        <ul class="help-legend">
          <li>☕🍺🥚🌾🍯🍞🥛🧃 <strong>Items</strong>. <span style="color:#c44e52">Red-tinted</span> = negative in the current context; <span style="color:#55a868">green-tinted</span> = positive flip item; plain = stable.</li>
          <li><span style="display:inline-block;width:10px;height:10px;border:2px solid rgba(85,168,104,0.95);vertical-align:middle"></span> <strong>Solid green border</strong> — picked up into the basket.</li>
          <li><span style="display:inline-block;width:10px;height:10px;border:1.5px dashed rgba(76,114,176,0.7);vertical-align:middle"></span> <strong>Dashed blue border</strong> — duplicate encounter (type already collected → pickup was illegal, agent forced to skip).</li>
          <li><span style="display:inline-block;width:10px;height:10px;background:rgba(0,0,0,0.45);vertical-align:middle"></span> <strong>Dark tint, no border</strong> — first-time skip (agent chose not to pick it up).</li>
          <li><span style="display:inline-block;width:10px;height:10px;border:2px solid rgba(205,232,255,0.85);vertical-align:middle"></span> <strong>White ring</strong> — the item the agent is deciding on right now.</li>
          <li>◐ <strong>Context badge</strong> (bottom-left) — morning (amber) or evening (purple). The item value-tints flip with it.</li>
        </ul>

        <h3>Try it</h3>
        <ul class="help-list">
          <li>Click <strong>Pretrain before run</strong>, then <strong>Start</strong> — watch a competent (already-trained) agent from step 1 instead of the cold-start coin-flip phase.</li>
          <li>Switch the arm to <strong>linear</strong> mid-run: on flip items it picks up the wrong half — its basket quality collapses.</li>
          <li>Switch theme to <strong>all_day</strong>: the context flips world-to-world. Watch the agent's pickup pattern flip with it once the model is trained.</li>
          <li>Hit <strong>Run train/eval</strong> to reproduce the validation numbers in-browser (4 arms × 5 seeds × train/eval split + flip schedule).</li>
        </ul>

        <a class="essay-btn" href="${ESSAY_URL}" target="_blank" rel="noopener">Read the post →</a>
      </div>
    `;
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.toggle();
    });
    this.el.querySelector<HTMLButtonElement>('.help-close')!.addEventListener('click', () => this.toggle());
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.mounted) this.toggle();
    };
    document.addEventListener('keydown', this.onKey);
  }

  toggle(): void {
    if (this.mounted) {
      this.el.remove();
      this.mounted = false;
    } else {
      document.body.appendChild(this.el);
      this.mounted = true;
    }
  }
}
